import { ExcelButton } from "@/controls/buttons/Excel-button";
import { SlagReportControl } from "@/controls/slag-report-control/SlagReportControl";
import {
	SimpleSelectorControl,
	getMomentTz,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import {
	Checkbox,
	FormControlLabel,
	FormGroup,
	Grid,
	Paper,
} from "@material-ui/core";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment-timezone";
import { DynamicGenericReportFilter } from "../../models/GenericReportFilter";
import {
	GenericReportFilterDataSource,
	GenericReportMetadataFilter,
} from "../../models/GenericReportMetadataFilter";
import { useGenericReportContext } from "../GenericReportContext";

type Props = {
	metadataFilters: GenericReportMetadataFilter[];
	filters: DynamicGenericReportFilter;
	filterSources: GenericReportFilterDataSource[][];
	updateFilters: (newValue: DynamicGenericReportFilter) => void;
	refreshData: () => void;
	onExcelExport: () => void;
};

const moment = getMomentTz();

export const ReportFilterPanel = ({
	metadataFilters,
	filters,
	filterSources,
	updateFilters,
	onExcelExport,
}: Props) => {
	const {
		state: { settings },
	} = useUTLSettingsContext();
	const { state } = useGenericReportContext();
	const isSlagReport = state.reportCode === "mes-lab-slag-samples";
	const filteredMetadata = metadataFilters.filter(
		(m) => m.Parameter !== "IncludeExternal",
	);
	const gridColumns = filteredMetadata.length === 1 ? 12 : 6;
	const filtersSpace = filteredMetadata.length === 1 ? 2 : 3;
	const isDateInvalid = moment(filters.startDate).isAfter(filters.endDate);

	const getDataSource = (index: number): any[] => {
		const result: any[] = [];
		filterSources[index]?.forEach((item) => {
			result.push({
				id: item.Id,
				description: item.Name,
			});
		});
		return result;
	};

	const onChangeFilter = (value: any, key: string) => {
		const newValues: DynamicGenericReportFilter = {
			...filters,
		};
		if (key === "startDate" || key === "endDate") newValues[key] = value;
		else newValues[key] = value;
		updateFilters(newValues);
	};

	return (
		<Grid container spacing={2} style={{ margin: "10px 0px 3px 0px" }}>
			<Grid item xs={12}>
				<Grid container spacing={2} alignItems="center">
					{filters.showStartDate && filters.showEndDate && (
						<Paper
							style={{
								display: "flex",
								justifyContent: "center",
								padding: "10px",
							}}
						>
							<Grid container spacing={2}>
								<Grid item xs={6}>
									<DatePicker
										label="Start"
										format="MM/DD/YYYY"
										value={moment(filters.startDate) as any}
										onChange={(value: Moment | null) => {
											if (value) onChangeFilter(value.toDate(), "startDate");
										}}
										disableFuture
										maxDate={moment(filters.endDate) as any}
										slotProps={{
											textField: {
												variant: "outlined",
												size: "small",
												inputProps: { style: { width: "110px" } },
												error: isDateInvalid,
												helperText: isDateInvalid
													? "Start must be before end"
													: "",
											},
										}}
									/>
								</Grid>
								<Grid item xs={6}>
									<DatePicker
										label="End"
										format="MM/DD/YYYY"
										value={moment(filters.endDate) as any}
										onChange={(value: Moment | null) => {
											if (value) onChangeFilter(value.toDate(), "endDate");
										}}
										disableFuture
										minDate={moment(filters.startDate) as any}
										slotProps={{
											textField: {
												variant: "outlined",
												size: "small",
												inputProps: { style: { width: "110px" } },
											},
										}}
									/>
								</Grid>
							</Grid>
						</Paper>
					)}

					{metadataFilters.some((m) => m.Parameter === "IncludeExternal") && (
						<Grid item>
							<FormGroup>
								<FormControlLabel
									control={
										<Checkbox
											checked={!!filters.IncludeExternal}
											color="primary"
											onChange={(event) => {
												onChangeFilter(event.target.checked, "IncludeExternal");
											}}
										/>
									}
									label="Include external tests"
								/>
							</FormGroup>
						</Grid>
					)}

					{isSlagReport ? (
						<SlagReportControl settings={settings} />
					) : (
						!isSlagReport &&
						metadataFilters.filter((m) => m.Parameter !== "IncludeExternal")
							.length > 0 && (
							<Grid item xs={12} md={filtersSpace}>
								<Paper style={{ padding: "8px" }}>
									<Grid container spacing={2} alignItems="center">
										{filteredMetadata.map((item, index) => (
											<Grid item xs={12} md={gridColumns} key={`grid_${index}`}>
												<SimpleSelectorControl
													title={item.FilterName}
													dataSource={getDataSource(index)}
													onChange={(value: string) =>
														onChangeFilter(value, item.Parameter)
													}
													selectedValue={filters[item.Parameter]}
													styleList={{ width: "100%" }}
												/>
											</Grid>
										))}
									</Grid>
								</Paper>
							</Grid>
						)
					)}

					<Grid item style={{ marginLeft: "auto", marginRight: "20px" }}>
						<ExcelButton onClick={onExcelExport} />
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
